import { default as React, useMemo, useState } from 'react';
import {
  makeStyles,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Box,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { InfoCard } from '@backstage/core-components';
import { QueryResponse } from '../../types/grafana';
import { transformGrafanaTableResponse } from '../../utils/dataTransformer';
import { groupErrorsByMessage, GroupedError } from '../../utils/errorGrouping';
import LogDialog from '../common/LogDialog';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  list: {
    overflow: 'auto',
    maxHeight: '400px',
    padding: 0,
  },
  listItem: {
    borderLeft: '4px solid transparent',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  errorItem: {
    borderLeftColor: theme.palette.error.main,
  },
  messageText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  messageContainer: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(1),
  },
  countChip: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  noData: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  lastRefreshed: {
    marginRight: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  viewButton: {
    padding: 4,
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  divider: {
    margin: `${theme.spacing(0.5)}px 0`,
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
}));

interface BackstageTopErrorsProps {
  data: QueryResponse | null;
  onRefresh?: () => void;
  lastRefreshed?: Date | null;
  loading?: boolean;
}

export const BackstageTopErrors: React.FC<BackstageTopErrorsProps> = ({
  data,
  onRefresh,
  lastRefreshed,
  loading = false,
}) => {
  const classes = useStyles();
  const [selectedLog, setSelectedLog] = useState<GroupedError | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const topErrors = useMemo(() => {
    if (!data || !data.results) return [];

    try {
      const { rows } = transformGrafanaTableResponse(data);
      return groupErrorsByMessage(rows, 5);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao processar logs de erro do Backstage:', error);
      return [];
    }
  }, [data]);

  const formatLastRefreshed = () => {
    if (!lastRefreshed) return '';
    return `Atualizado em: ${lastRefreshed.toLocaleTimeString()}`;
  };

  const handleViewLog = (log: GroupedError) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  return (
    <InfoCard title="Top 5 Erros do Backstage" className={classes.root}>
      <div className={classes.header}>
        <Typography variant="caption" className={classes.lastRefreshed}>
          {formatLastRefreshed()}
        </Typography>
        {onRefresh && (
          <Tooltip title="Atualizar dados">
            <IconButton onClick={onRefresh} size="small" disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {topErrors.length === 0 ? (
        <Paper className={classes.noData}>
          <Typography variant="body2">
            Nenhum log de erro do Backstage encontrado no período selecionado.
          </Typography>
        </Paper>
      ) : (
        <List className={classes.list}>
          {topErrors.map((error, index) => (
            <React.Fragment key={`${error.message.substring(0, 20)}-${index}`}>
              {index > 0 && <Divider component="li" className={classes.divider} />}
              <ListItem className={`${classes.listItem} ${classes.errorItem}`}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" width="100%">
                      <Box display="flex" alignItems="center" className={classes.messageContainer}>
                        <Tooltip title={error.message}>
                          <Typography variant="body2" className={classes.messageText}>
                            {error.message}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box className={classes.actionsContainer}>
                        <Chip
                          label={`${error.count}x`}
                          size="small"
                          className={classes.countChip}
                        />
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            className={classes.viewButton}
                            size="small"
                            onClick={() => handleViewLog(error)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" className={classes.timestamp}>
                      Último registro: {new Date(error.timestamp).toLocaleString()}
                    </Typography>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      <LogDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        log={selectedLog}
      />
    </InfoCard>
  );
};